/**
 * MinigameScreen.web.tsx
 * De webversie van de Ocean Battle!
 * Meer ruimte, dus we zetten ze naast elkaar.
 */

import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Animated,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {productService} from '../services';
import {ProductDisplay} from '../models';

interface MinigameCardProps {
  product: ProductDisplay;
  gameState: 'playing' | 'result';
  winnerId: number | null;
  selectedId: number | null;
  onPress: () => void;
  t: any;
}

const MinigameCardWeb: React.FC<MinigameCardProps> = ({ 
  product, 
  gameState, 
  winnerId, 
  selectedId, 
  onPress,
  t
}) => {
  const [imageError, setImageError] = useState(false);
  
  const isSelected = selectedId === product.id;
  const isTheWinner = gameState === 'result' && winnerId === product.id;
  const isTheLoser = gameState === 'result' && winnerId !== product.id;

  return (
    <TouchableOpacity
      style={[
        stijlen.card,
        gameState === 'result' && isTheWinner && stijlen.cardCorrect,
        gameState === 'result' && isTheLoser && stijlen.cardWrong,
        gameState === 'playing' && stijlen.cardHover,
      ]}
      onPress={onPress}
      disabled={false}
      activeOpacity={0.9}
    >
      <View style={stijlen.imagePlaceholder}>
        {product.imageUrl && !imageError ? (
            // @ts-ignore
          <img 
            src={product.imageUrl} 
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
            alt={product.name}
            onError={() => setImageError(true)}
          />
        ) : (
          <Text style={{fontSize: 60}}>🧴</Text>
        )}
        
        {gameState === 'result' && (
          <View style={[
            stijlen.resultBadge,
            {backgroundColor: isTheWinner ? '#10B981' : '#EF4444'}
          ]}>
            <Text style={stijlen.resultEmoji}>
              {isTheWinner ? '🏆' : '😢'}
            </Text>
            <Text style={stijlen.gradeReveal}>
              {product.grade}
            </Text>
            <Text style={stijlen.clickForInfo}>
               ℹ️ {t('common.moreInfo', 'More info')}
            </Text>
          </View>
        )}
      </View>

      <View style={stijlen.cardInfo}>
        <Text style={stijlen.productName}>{product.name}</Text>
        <Text style={stijlen.productBrand}>{product.brand}</Text>
      </View>
    </TouchableOpacity>
  );
};

const MinigameScreen: React.FC<any> = ({ navigation }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [gameState, setGameState] = useState<'playing' | 'result'>('playing');
  const [winnerId, setWinnerId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const loadRound = async () => {
    setLoading(true);
    setGameState('playing');
    setWinnerId(null);
    setSelectedId(null);
    
    const pair = await productService.getComparisonPair();
    setProducts(pair);
    setLoading(false);
  };

  useEffect(() => {
    loadRound();
  }, []);

  const handleGuess = (myProduct: ProductDisplay) => {
    if (gameState !== 'playing') return;

    setSelectedId(myProduct.id);
    const otherProduct = products.find(p => p.id !== myProduct.id)!;
    
    const getGradeValue = (grade: string) => {
      const values: Record<string, number> = {
        'A+': 15, 'A': 14, 'A-': 13,
        'B+': 12, 'B': 11, 'B-': 10,
        'C+': 9, 'C': 8, 'C-': 7,
        'D+': 6, 'D': 5, 'D-': 4,
        'E+': 3, 'E': 2, 'E-': 1,
      };
      return values[grade] || 0;
    };

    const myVal = getGradeValue(myProduct.grade);
    const otherVal = getGradeValue(otherProduct.grade);

    const realWinner = myVal >= otherVal ? myProduct.id : otherProduct.id;
    setWinnerId(realWinner);

    if (myVal >= otherVal) {
      setScore(s => s + 10);
      setStreak(s => s + 1);
    } else {
      setStreak(0);
    }

    setGameState('result');
  };

  if (loading && products.length === 0) {
    return (
      <View style={stijlen.loadingContainer}>
        <ActivityIndicator size="large" color="#7B3FF2" />
        <Text style={stijlen.loadingText}>{t('common.loading')}</Text>
      </View>
    );
  }

  if (products.length === 0) {
    return (
      <View style={stijlen.loadingContainer}>
        <Text style={[stijlen.loadingText, { marginBottom: 20 }]}>
            {t('errors.databaseQueryError') || 'Er is te weinig data om te spelen.'}
        </Text>
        <TouchableOpacity style={stijlen.continueButton} onPress={loadRound}>
            <Text style={stijlen.continueText}>{t('common.retry')}</Text>
        </TouchableOpacity>
        <TouchableOpacity 
            style={[stijlen.backArrow, { marginTop: 20, backgroundColor: '#6366F1' }]} 
            onPress={() => navigation.goBack()}
        >
            <Text style={stijlen.backText}>{t('common.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={stijlen.container}>
      <View style={stijlen.header}>
        <View style={stijlen.headerContent}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={stijlen.backArrow}>
            <Text style={stijlen.backText}>← {t('common.back', 'Terug')}</Text>
          </TouchableOpacity>
          <Text style={stijlen.gameTitle}>🌊 Ocean Battle</Text>
          <View style={stijlen.stats}>
            <View style={stijlen.statPill}>
              <Text style={stijlen.statLabel}>SCORE</Text>
              <Text style={stijlen.statValue}>{score}</Text>
            </View>
            <View style={[stijlen.statPill, stijlen.streakPill]}>
              <Text style={stijlen.statLabel}>STREAK</Text>
              <Text style={stijlen.statValue}>🔥 {streak}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={stijlen.gameArea}>
        <Text style={stijlen.question}>Welk product is beter voor de oceaan?</Text>
        
        <View style={stijlen.cardsContainer}>
          {products.map((product) => (
            <MinigameCardWeb
              key={product.id}
              product={product}
              gameState={gameState}
              winnerId={winnerId}
              selectedId={selectedId}
              t={t}
              onPress={() => {
                if (gameState === 'playing') {
                  handleGuess(product);
                } else {
                  navigation.navigate('ProductDetail', { productId: product.id });
                }
              }}
            />
          ))}
        </View>

        {gameState === 'result' && (
          <View style={stijlen.resultContainer}>
             <View style={stijlen.abcContainer}>
              <Text style={stijlen.winnerTitle}>
                🏆 {products.find(p => p.id === winnerId)?.name} wins!
              </Text>
              <Text style={stijlen.winnerText}>
                {products.find(p => p.id === winnerId)?.overallReason}
              </Text>
             </View>

            <TouchableOpacity style={stijlen.continueButton} onPress={loadRound}>
              <Text style={stijlen.continueText}>Volgende Ronde ➡️</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

const stijlen = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100vh' as any,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    height: '100vh' as any,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 20,
    fontSize: 20,
    color: '#6366F1',
    fontWeight: 'bold',
  },
  header: {
    padding: 24,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    // @ts-ignore
    background: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)',
  },
  headerContent: {
    maxWidth: 900,
    marginHorizontal: 'auto',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  gameTitle: {
    fontSize: 28,
    color: 'white',
    fontWeight: '800',
  },
  stats: {
    flexDirection: 'row',
    gap: 16,
  },
  statPill: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    alignItems: 'center',
    minWidth: 100,
  },
  streakPill: {
    backgroundColor: 'rgba(255,165,0,0.3)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.5)',
  },
  statLabel: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: 'bold',
    letterSpacing: 1,
  },
  statValue: {
    fontSize: 20,
    color: 'white',
    fontWeight: '800',
  },
  backArrow: {
    marginRight: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    cursor: 'pointer',
    // @ts-ignore
    transition: 'background 0.2s',
  },
  backText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 14,
  },
  gameArea: {
    flex: 1,
    maxWidth: 900,
    marginHorizontal: 'auto',
    width: '100%',
    padding: 40,
    alignItems: 'center',
  },
  question: {
    fontSize: 32,
    color: '#1E293B',
    fontWeight: '700',
    marginBottom: 40,
    textAlign: 'center',
  },
  cardsContainer: {
    flexDirection: 'row',
    gap: 30,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  card: {
    width: 300,
    backgroundColor: 'white',
    borderRadius: 24,
    overflow: 'hidden',
    // @ts-ignore
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    borderWidth: 4,
    borderColor: 'transparent',
  },
  cardHover: {
    // @ts-ignore
    ':hover': {
      transform: 'translateY(-10px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
    }
  } as any,
  cardCorrect: {
    borderColor: '#10B981',
    backgroundColor: '#F0FDF4',
    transform: [{scale: 1.05}],
  },
  cardWrong: {
    borderColor: '#EF4444',
    opacity: 0.7,
  },
  imagePlaceholder: {
    height: 250,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  cardInfo: {
    padding: 24,
    alignItems: 'center',
  },
  productName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  productBrand: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  resultBadge: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(4px)',
  } as any,
  resultEmoji: {
    fontSize: 60,
  },
  gradeReveal: {
    color: 'white',
    fontSize: 40,
    fontWeight: '800',
    marginTop: 10,
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowRadius: 10,
  },
  clickForInfo: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 14,
    marginTop: 8,
    fontWeight: '600',
  },
  resultContainer: {
    alignItems: 'center',
    marginTop: 40,
    width: '100%',
  },
  abcContainer: {
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 16,
    maxWidth: 600,
    width: '100%',
    alignItems: 'center',
    marginBottom: 24,
    // @ts-ignore
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
  },
  winnerTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#10B981',
    marginBottom: 12,
    textAlign: 'center',
  },
  winnerText: {
    fontSize: 18,
    color: '#334155',
    textAlign: 'center',
    lineHeight: 28,
  },
  continueButton: {
    marginTop: 50,
    backgroundColor: '#6366F1',
    paddingHorizontal: 40,
    paddingVertical: 18,
    borderRadius: 50,
    // @ts-ignore
    boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)',
    transition: 'transform 0.2s',
  },
  continueText: {
    color: 'white',
    fontSize: 20,
    fontWeight: '700',
  },
});

export default MinigameScreen;
